import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "./supabaseClient";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import "./App.css";
import { useTranslation } from "react-i18next";
import StarRating from "./StarRating";
import { FiChevronLeft, FiTrash2 } from "react-icons/fi";
import CzDialog from "./CzDialog2";


const LogDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [log, setLog] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  // ⭐ feedback state
  const [rating, setRating] = useState(null); // 1..5 oppure null
  const [feedbackText, setFeedbackText] = useState("");
  const [ratingGlycemia, setRatingGlycemia] = useState(""); // facoltativa
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
    showCancel: false,
    confirmText: "OK",
    cancelText: "Annulla",
    onConfirm: null,
  });

  const openDialog = (cfg) => {
    setDialog({
      open: true,
      title: cfg.title || "",
      message: cfg.message || "",
      variant: cfg.variant || "info",
      showCancel: !!cfg.showCancel,
      confirmText: cfg.confirmText || "OK",
      cancelText: cfg.cancelText || "Annulla",
      onConfirm: cfg.onConfirm || null,
    });
  };

  const closeDialog = () =>
    setDialog((d) => ({ ...d, open: false, onConfirm: null }));

  const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "#FFD24D",
          padding: "8px 12px",
          borderRadius: "10px",
          border: "1px solid #3390B3",
          color: "#3390B3",
          fontWeight: 600,
          fontSize: "0.9rem",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        {payload[0].name}: {payload[0].value} g
      </div>
    );
  }
  return null;
};

const isNativeCapacitor = () => {
  return !!(window?.Capacitor?.isNativePlatform?.() || window?.Capacitor?.isNative);
};

const notifIdFor = (logId, type) => Number(logId) * 10 + Number(type); // type: 1 bolo, 2 review

const cancelNotifsForLog = async (logId) => {
  if (!isNativeCapacitor() || !logId) return;

  try {
    const mod = await import("@capacitor/local-notifications");
    const { LocalNotifications } = mod;

    await LocalNotifications.cancel({
      notifications: [
        { id: notifIdFor(logId, 1) },
        { id: notifIdFor(logId, 2) },
      ],
    });

    console.log(`[notifs] canceled for log ${logId}`);
  } catch (e) {
    console.error("[notifs] cancel error:", e);
  }
};


  useEffect(() => {
    const fetchLog = async () => {
      const { data, error } = await supabase
        .from("insulin_logs")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Errore nel recupero del log:", error.message);
        return;
      }

      // ✅ se è stato "cancellato", torna subito alla lista
      if (data?.is_visible === false) {
        navigate("/stats");
        return;
      }

      setLog(data);
      if (data.image_url) setImageUrl(data.image_url);

      // ⭐ inizializza feedback (usa SOLO campi esistenti nello schema)
      setRating(data.rating ?? null);
      setFeedbackText(data.feedback_text ?? "");
      setRatingGlycemia(
        data.rating_glicemia !== null && data.rating_glicemia !== undefined
          ? String(data.rating_glicemia)
          : ""
      );
    };

    fetchLog();
  }, [id, navigate]);

  const doSoftDelete = async () => {
    if (!log?.id) return;

    setDeleting(true);

    // ✅ cancella eventuali notifiche schedulate per questo log (solo su app native)
    await cancelNotifsForLog(log.id);

    const { error } = await supabase
      .from("insulin_logs")
      .update({ is_visible: false })
      .eq("id", log.id);

    if (error) {
      console.error("Errore eliminazione:", error.message);
      setDeleting(false);

      openDialog({
        title: t("attenzione") || "Attenzione",
        message: t("erroreEliminazioneAnalisi"),
        variant: "warning",
        showCancel: false,
        confirmText: "OK",
      });
      return;
    }

    navigate("/stats");
  };

  const softDeleteLog = () => {
    if (!log?.id || deleting) return;

    openDialog({
      title: t("eliminaAnalisi") || "Elimina analisi",
      message: t("confermaEliminazioneAnalisi"),
      variant: "danger",
      showCancel: true,
      confirmText: t("elimina") || "Elimina",
      cancelText: t("annulla") || "Annulla",
      onConfirm: doSoftDelete,
    });
  };


  const roundHalf = (num) => {
    if (!Number.isFinite(num) || num <= 0) return 0;

    // sotto 0.25 UI → clinicamente 0
    if (num < 0.25) return 0;

    return Math.round(num * 2) / 2;
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    const utcString = isoString.endsWith("Z") ? isoString : isoString + "Z";
    const dateUTC = new Date(utcString);

    return dateUTC.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Europe/Rome",
    });
  };

  const saveFeedback = async () => {
    if (!log?.id) return;

    setFeedbackMsg("");

    // ✅ obbligatorio: stelle
    if (!rating || Number(rating) < 1) {
      setFeedbackMsg(t("selezionaStelle"));
      return;
    }

    setSavingFeedback(true);

    const gly = ratingGlycemia !== "" ? Number(ratingGlycemia) : null;
    if (gly !== null && (!Number.isFinite(gly) || gly <= 0)) {
      setSavingFeedback(false);
      setFeedbackMsg(t("valoreGlicemia"));
      return;
    }

    const payload = {
      rating: Number(rating),
      feedback_text: feedbackText.trim() ? feedbackText.trim() : null,
      rating_glicemia: gly, // ✅ facoltativa
      feedback_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("insulin_logs")
      .update(payload)
      .eq("id", log.id)
      .select()
      .single();

    if (error) {
      console.error("Errore salvataggio feedback:", error.message);
      setFeedbackMsg(t("erroreSalvataggio"));
    } else {
      setLog(data);
      setFeedbackMsg(t("salvato"));
    }

    setSavingFeedback(false);
  };

  if (!log) return <p className="instructions">{t("caricamento")}</p>;

  const pieData = [
    { name: t("carboidrati"), value: parseFloat(log.carboidrati) || 0 },
    { name: t("proteine"), value: parseFloat(log.proteine) || 0 },
    { name: t("grassi"), value: parseFloat(log.grassi) || 0 },
  ];
  const COLORS = ["#3390B3", "#2ECC71", "#E67E22"];

  const formattedDate = formatTime(log.created_at);

  // ✅ mostra blocchi solo se c'è contenuto
  const hasDescription = !!(
    log?.descrizione_inserita && String(log.descrizione_inserita).trim()
  );
  const hasNote = !!(log?.note && String(log.note).trim());
  const charmDetected = !!log?.charm_detected;
  const coinDetected = !!log?.coin_detected;
  const hasScaleDetected = charmDetected || coinDetected;

  // ✅ value helper (numeri più grandi delle label)
  const ValueBox = ({ value, unit }) => (
    <div
      className="glicemia-input2"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "baseline",
        gap: 6,
        width: "100%",
      }}
    >
      <span style={{ fontWeight: 800, fontSize: "1.25rem" }}>
        {value ?? "-"}
      </span>
      {unit ? (
        <span style={{ fontSize: "0.95rem", fontWeight: 700, opacity: 0.9 }}>
          {unit}
        </span>
      ) : null}
    </div>
  );


  return (
    <div className="container">
      <CzDialog open={dialog.open} title={dialog.title} message={dialog.message} variant={dialog.variant} showCancel={dialog.showCancel} confirmText={dialog.confirmText} cancelText={dialog.cancelText} onCancel={closeDialog}
        onConfirm={() => {
          const fn = dialog.onConfirm;
          closeDialog();
          fn?.();
        }}
      />
      <div className="header-with-button">
        <h1 className="title">{t("schedaPasto")}</h1>
        <button type="button" className="icon-nav" onClick={() => navigate(-1)} aria-label={t("indietro")} >
          <FiChevronLeft size={34} />
        </button>
      </div>

      <div className="card-section card-blue" style={{ position: "relative" }}>
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: 0,
          }}
        >
          <h2 className="titlesec center" style={{ margin: 0 }}>
            {formattedDate}
          </h2>

          <button
            type="button"
            onClick={softDeleteLog}
            disabled={deleting}
            aria-label={t("eliminaAnalisi")}
            title={t("eliminaAnalisi")}
            style={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)", // 🔥 chiave
              width: 40,
              height: 40,
              borderRadius: 12,
              border: "2px solid rgba(220, 38, 38, 0.55)",
              background: "rgba(255,255,255,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: deleting ? "default" : "pointer",
              opacity: deleting ? 0.6 : 1,
            }}
          >
            <FiTrash2 size={20} color="#dc2626" />
          </button>
        </div>


        {/* ✅ RIGA 1: 2 colonne (Bolo standard + UGP) */}
        <div className="settings-row-2" style={{ marginTop: 6 }}>
          <div>
            <div className="form-label settings-mini-label center">
              {t("insulinaCalcolata")}
            </div>
            <div className="input-wrapper input-wrapper-md">
              <ValueBox value={roundHalf(log.insulina_calcolata)} unit="u" />
            </div>
          </div>

          <div>
            <div className="form-label settings-mini-label center">
              {t("insulinaAggiuntivaPankowska")}
            </div>
            <div className="input-wrapper input-wrapper-md">
              <ValueBox value={roundHalf(log.insulina_kowalska)} unit="u" />
            </div>
          </div>
        </div>

        <div className="settings-divider" style={{ margin: "14px 0" }} />

        {/* ✅ RIGA 2: 3 colonne (Carboidrati / Proteine / Grassi) */}
        <div className="settings-row-3">
          <div>
            <div className="form-label settings-mini-label center">
              {t("carboidrati")}
            </div>
            <div className="input-wrapper input-wrapper-sm">
              <ValueBox value={log.carboidrati ?? "-"} unit="g" />
            </div>
          </div>

          <div>
            <div className="form-label settings-mini-label center">
              {t("proteine")}
            </div>
            <div className="input-wrapper input-wrapper-sm">
              <ValueBox value={log.proteine ?? "-"} unit="g" />
            </div>
          </div>

          <div>
            <div className="form-label settings-mini-label center">
              {t("grassi")}
            </div>
            <div className="input-wrapper input-wrapper-sm">
              <ValueBox value={log.grassi ?? "-"} unit="g" />
            </div>
          </div>
        </div>

        {/* ✅ RIGA 3: 2 colonne (Glicemia / Calorie) */}
        <div className="settings-row-2" style={{ marginTop: 10 }}>
          <div>
            <div className="form-label settings-mini-label center">
              {t("glicemia")}
            </div>
            <div className="input-wrapper input-wrapper-md">
              <ValueBox value={log.glicemia ?? "-"} unit="mg/dL" />
            </div>
          </div>

          <div>
            <div className="form-label settings-mini-label center">
              {t("calorie")}
            </div>
            <div className="input-wrapper input-wrapper-md">
              <ValueBox value={log.calorie ?? "-"} unit="kcal" />
            </div>
          </div>
        </div>

        {imageUrl ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "1rem",
              width: "100%",
            }}
          >
            <img
              src={imageUrl}
              alt="Foto pasto"
              className="preview-image"
              style={{
                maxWidth: "100%",
                height: "auto",
              }}
            />
          </div>
        ) : (
          <p className="instructions">📷 {t("nessunaFoto")}</p>
        )}

        <p className="instructions">{log.risultato_ai || "-"}</p>

        {hasDescription && (
          <p className="instructions">
            <b>{t("descrizioneInserita")}</b> {log.descrizione_inserita}
          </p>
        )}

        {hasNote && (
          <p className="instructions">
            <b>{t("note")}</b> {log.note}
          </p>
        )}

        {hasScaleDetected && (
          <div
            style={{
              marginTop: 10,
              marginBottom: 10,
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
            }}
          >
            {charmDetected && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 700, color: "#3390B3" }}>
                <img
                  src="/icona.png"
                  alt=""
                  style={{ width: 24, height: 24, display: "block" }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none"; // se manca, almeno non mostra l'icona rotta
                  }}
                />
                {t("charmDetected")}
              </div>
            )}

            {coinDetected && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 700, color: "#3390B3" }}>
                <img
                  src="/icona.png"
                  alt=""
                  style={{ width: 24, height: 24, display: "block" }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                {t("coinDetected")}
              </div>
            )}
          </div>
        )}



        {/* 📊 Grafico */}
        <div
          className="chart-container"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            width: "100%",
            marginTop: "1rem",
          }}
        >
          <h2 style={{ textAlign: "center", color: "#3390B3"}} className="titlesec">
            {t("distribuzioneMacronutrienti")}
          </h2>

          <PieChart width={260} height={260}>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={90}
              label={false}
              labelLine={false}
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend verticalAlign="bottom" />
          </PieChart>
        </div>

        {/* ⭐ Valutazione */}
          <div
            className={`review-card ${!rating ? "shimmer-border" : ""}`}
            style={{
              marginTop: 18,
              padding: 14,
              borderRadius: 14,
              background: "#ffd24d",
              border: "2px solid #3390B3",
              position: "relative",
            }}
          >
          <p className="instructions" style={{ margin: 0, textAlign: "center" }}>
            <b>{t("valutaAnalisi")}</b>
          </p>

          <div
            style={{ marginTop: 10, display: "flex", justifyContent: "center" }}
          >
            <StarRating value={rating || 0} onChange={(v) => setRating(v)} />
          </div>

          {/* 🩸 glicemia facoltativa */}
          <p
            className="instructions"
            style={{ marginTop: 12, marginBottom: 6, textAlign: "center" }}
          >
            <b>{t("glicemiaAttualeFacoltativa")}</b>
          </p>

          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              className={rating && !ratingGlycemia ? "pulse-wrap" : ""}
              style={{ borderRadius: 12, display: "inline-block" }}
            >
              <input
                type="number"
                placeholder="mg/dL"
                value={ratingGlycemia}
                onChange={(e) => setRatingGlycemia(e.target.value)}
                className="glicemia-input2"
                style={{
                  maxWidth: 220,
                  textAlign: "center",
                  fontWeight: 700,
                }}
              />
            </div>
          </div>

          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder={t("commentoFacoltativo")}
            className="glicemia-input2"
            style={{
              marginTop: 10,
              minHeight: 100,
              resize: "vertical",
              paddingTop: 12,
              paddingBottom: 12,
              fontFamily: "inherit",
            }}
          />

          <button
            onClick={saveFeedback}
            disabled={savingFeedback}
            className="custom-button4"
            style={{ marginTop: 10, width: "100%" }}
          >
            {savingFeedback ? t("salvataggio") : t("salvaValutazione")}
          </button>

          {feedbackMsg && (
            <p
              className="instructions"
              style={{ marginTop: 8, marginBottom: 0, textAlign: "center" }}
            >
              {feedbackMsg}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogDetail;
